import { t } from "i18next";
import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { PlanningWeek, PlanningWeekContent } from "../../../../interfaces/planning";

/**
 * Props for the ContentEditModal component
 * @property show - Controls modal visibility
 * @property week - The planning week for which content is being edited
 * @property fieldKey - The field key (e.g., 'R0', 'K1') being edited
 * @property fieldName - The display name of the field
 * @property existingContent - Existing content data if editing
 * @property onClose - Callback when modal is closed
 * @property onSave - Callback when content is saved, receives value, color, and observations
 */
interface ContentEditModalProps {
    show: boolean;
    week: PlanningWeek | null;
    fieldKey: string;
    fieldName: string;
    existingContent: PlanningWeekContent | null;
    onClose: () => void;
    onSave: (value: string, color: string, observations: string) => void;
}

/**
 * Modal component for editing content distribution values in a planning week.
 * Allows users to set a numeric value, choose a color, and add observations.
 */
export function ContentEditModal(props: ContentEditModalProps) {
    // Form fields
    const [value, setValue] = useState('');
    const [color, setColor] = useState('#ffffff');
    const [observations, setObservations] = useState('');

    /**
     * Load existing content when modal opens or content changes
     */
    useEffect(() => {
        if (props.existingContent) {
            setValue(props.existingContent.value || '');
            setColor(props.existingContent.color || '#ffffff');
            setObservations(props.existingContent.observations || '');
        } else {
            setValue('');
            setColor('#ffffff');
            setObservations('');
        }
    }, [props.existingContent, props.show]);

    /**
     * Saves the content and calls parent callback
     */
    const handleSave = () => {
        props.onSave(value.trim(), color, observations.trim());
        handleClose();
    };

    /**
     * Closes the modal and resets form
     */
    const handleClose = () => {
        props.onClose();
    };

    /**
     * Formats the date range for display in the modal header
     */
    const formatDateRange = () => {
        if (!props.week) return '';

        const fromDate = new Date(props.week.from);
        const toDate = new Date(props.week.to);

        const fromMonth = fromDate.toLocaleDateString('es-ES', { month: 'short' });
        const toMonth = toDate.toLocaleDateString('es-ES', { month: 'short' });

        const fromDay = props.week.from_name;
        const toDay = props.week.to_name;

        if (fromMonth === toMonth) {
            return `${fromDay} ${t("to")} ${toDay} de ${toMonth}`;
        } else {
            return `${fromDay} de ${fromMonth} ${t("to")} ${toDay} de ${toMonth}`;
        }
    };

    return (
        <Modal show={props.show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {t("Edit Content")} - {props.fieldName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <h6 className="text-muted">
                        {t("Week")} {props.week?.key}
                        {props.week && (
                            <small className="ms-2">
                                ({formatDateRange()})
                            </small>
                        )}
                    </h6>
                </div>

                <Form>
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <Form.Group controlId="contentValue">
                                <Form.Label>{t("Value")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.1"
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    placeholder="0.0"
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group controlId="contentColor">
                                <Form.Label>{t("Background color")}</Form.Label>
                                <Form.Control
                                    type="color"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    style={{ width: '60px', height: '40px' }}
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <Form.Group className="mb-3" controlId="contentObservations">
                        <Form.Label>{t("Observations")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={observations}
                            onChange={(e) => setObservations(e.target.value)}
                            placeholder={t("Additional notes or comments")}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    {t("Cancel")}
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    {t("Save")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
